// Type 2 — "Open in Claude" orchestration (React side).
// React fetches the selected datasets from Atlas (per Aman's design), converts
// them to CSV, then hands them to Tauri to write the project folder + skills.

import { invoke } from '@tauri-apps/api/core';
import graphqlClient from './graphqlClient';
import { environment } from '../environment/environment';

export interface DatasetDef {
  key: string;        // unique id
  label: string;      // shown in the modal
  file: string;       // output CSV filename
  columns: string[];  // columns to export
  description: string; // for Claude.md
  // Table-driven export (default path): Hasura table + filter + stable sort
  table?: string;
  where?: (brand: string, isUltimateParent: boolean) => string;
  orderBy?: string;   // stable sort key so pagination never skips/duplicates rows
  // Custom fetcher for sources that aren't a single Hasura table (e.g. actions,
  // multi-step joins). Overrides the table query when present.
  fetch?: (brand: string, isUltimateParent: boolean) => Promise<any[]>;
}

// The export manifest — single source of truth for which CSVs come from where.
export const DATASETS: DatasetDef[] = [
  {
    key: 'brand_summary',
    label: 'Brand Summary',
    file: 'brand_summary.csv',
    table: 'udm_brand',
    columns: ['name', 'ultimate_parent', 'brand_segment', 'retailer_segment',
              'is_top_retailer', 'estimated_no_of_carts_per_store',
              'purchek_install_estimation', 'containment_install_estimation'],
    where: (b, up) => up
      ? `{ ultimate_parent: { _ilike: "%${b}%" } }`
      : `{ name: { _ilike: "%${b}%" } }`,
    orderBy: '{ name: asc }',
    description: 'Brand-level rollup — segment, parent company, install estimates.',
  },
  {
    key: 'stores',
    label: 'Stores',
    file: 'stores.csv',
    table: 'udm_poi',
    columns: ['id', 'address', 'brand_name', 'zipcode', 'channel', 'status',
              'rank', 'purchek_rank', 'containment_rank', 'cart_manager_rank',
              'purchek_install_date', 'containment_install_date',
              'lifetime_value', 'customer_id', 'latitude', 'longitude'],
    where: (b, up) => up
      ? `{ brand: { ultimate_parent: { _ilike: "%${b}%" } } }`
      : `{ brand_name: { _ilike: "%${b}%" } }`,
    orderBy: '{ id: asc }',
    description: 'One row per store — address, status, ranks, install dates, recommended solution.',
  },
  {
    key: 'penetration',
    label: 'Penetration & Loss Impact',
    file: 'penetration.csv',
    table: 'udm_nearby_penetration',
    columns: ['poi_id', 'penetration', 'penetration_purchek', 'penetration_containment',
              'total_neighbors', 'shoplifting_amount_usd', 'shoplifting_incident_count',
              'plaza_cluster'],
    where: (b, up) => up
      ? `{ poi: { brand: { ultimate_parent: { _ilike: "%${b}%" } } } }`
      : `{ poi: { brand_name: { _ilike: "%${b}%" } } }`,
    orderBy: '{ poi_id: asc }',
    description: 'Penetration % and shoplifting impact per store (join to stores via poi_id ↔ id).',
  },
  {
    key: 'opportunities',
    label: 'CRM Opportunities',
    file: 'opportunities.csv',
    columns: ['OpportunityId', 'Topic', 'PotentialCustomer', 'EstRevenue', 'PricePerStore',
              'OpportunitySiteType', 'EstimatedCloseDate', 'CreatedOn'],
    fetch: fetchCrmOpportunities,
    description: 'Active CRM deals for the brand — topic, estimated revenue, price per store, close date.',
  },
  {
    key: 'revenue',
    label: 'Revenue History',
    file: 'revenue.csv',
    columns: ['customer_id', 'year', 'invoices', 'revenue_usd'],
    fetch: fetchRevenue,
    description: 'Invoiced revenue per acquired store per year (join to stores via customer_id).',
  },
  {
    key: 'wheels',
    label: 'Wheel Fleet',
    file: 'wheels.csv',
    table: 'udm_wheel',
    columns: ['customer_id', 'total_wheels', 'no_of_active_wheels', 'healthy_wheels',
              'dead_wheels', 'missing_wheels', 'zombie_wheels', 'used_wheels',
              'total_paired_wheels', 'no_of_wheels_needs_replacement',
              'single_wheel_replacement_rate', 'service_frequency',
              'wheel_install_date', 'paired_wheel_install_date', 'last_wheel_activity'],
    where: (b, up) => up
      ? `{ poi: { brand: { ultimate_parent: { _ilike: "%${b}%" } } } }`
      : `{ poi: { brand_name: { _ilike: "%${b}%" } } }`,
    orderBy: '{ customer_id: asc }',
    description: 'Wheel fleet health per store — totals, active/dead/missing counts, replacement rate (join to stores via customer_id).',
  },
  {
    key: 'video',
    label: 'Video Incidents',
    file: 'video.csv',
    table: 'udm_video',
    columns: ['customer_id', 'incident_count', 'total_crime_amount_usd'],
    where: (b, up) => up
      ? `{ poi: { brand: { ultimate_parent: { _ilike: "%${b}%" } } } }`
      : `{ poi: { brand_name: { _ilike: "%${b}%" } } }`,
    orderBy: '{ customer_id: asc }',
    description: 'Video-recorded incident counts and crime value per store (join to stores via customer_id).',
  },
  {
    key: 'geo',
    label: 'Geo & Local Crime',
    file: 'geo.csv',
    table: 'udm_geo',
    columns: ['zipcode', 'city', 'county', 'state', 'state_code', 'crime_rate',
              'total_crime_incidents', 'containment_required', 'penalty_for_cart_owner',
              'law_summary', 'law_source', 'annual_precipitation',
              'no_of_hot_days', 'no_of_cold_days'],
    where: (b, up) => up
      ? `{ pois: { brand: { ultimate_parent: { _ilike: "%${b}%" } } } }`
      : `{ pois: { brand_name: { _ilike: "%${b}%" } } }`,
    orderBy: '{ zipcode: asc }',
    description: 'Zipcode-level context for areas with the brand\'s stores — crime rate, cart laws, climate (join to stores via zipcode).',
  },
  {
    key: 'products',
    label: 'Product Catalog',
    file: 'products.csv',
    table: 'udm_item',
    columns: ['id', 'category', 'sub_category', 'type', 'description'],
    where: () => '{}',
    orderBy: '{ id: asc }',
    description: 'Gatekeeper product/part catalog — global reference, not brand-specific.',
  },
  {
    key: 'updated_store_ids',
    label: 'Updated Store IDs',
    file: 'updated_store_ids.csv',
    columns: ['id', 'poi_id', 'store_id', 'brand_name', 'address', 'zipcode'],
    fetch: fetchUpdatedStoreIds,
    description: 'Store-identifier updates/corrections for the brand\'s banners.',
  },
];

// Page size for pagination — all rows are fetched, this only bounds each request.
const PAGE_SIZE = 1000;

function toCSV(columns: string[], rows: any[]): string {
  const esc = (v: any) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.join(',');
  const body = rows.map(r => columns.map(c => esc(r[c])).join(',')).join('\n');
  return header + '\n' + body + '\n';
}

/** Paginate a Hasura table query until exhausted. */
async function fetchAllRows(table: string, where: string, orderBy: string, fields: string[]): Promise<any[]> {
  const all: any[] = [];
  for (let offset = 0; ; offset += PAGE_SIZE) {
    const query = `query {
      ${table}(where: ${where}, order_by: ${orderBy}, limit: ${PAGE_SIZE}, offset: ${offset}) {
        ${fields.join(' ')}
      }
    }`;
    const data = await graphqlClient.request<Record<string, any[]>>(query);
    const page = data[table] || [];
    all.push(...page);
    if (page.length < PAGE_SIZE) break;
  }
  return all;
}

async function fetchDataset(d: DatasetDef, brand: string, isUP: boolean) {
  if (d.fetch) return d.fetch(brand, isUP);
  return fetchAllRows(d.table!, d.where!(brand, isUP), d.orderBy!, d.columns);
}

/** CRM opportunities come from a Hasura action, not a table. */
async function fetchCrmOpportunities(brand: string, isUP: boolean): Promise<any[]> {
  // The action filters by ultimate parent; for a sub-brand fall back to Account.
  const arg = isUP ? 'UltimateParent' : 'Account';
  const query = `query {
    getCrmOpportunities(${arg}: "${brand}", Top: 10000) {
      Status ErrorMessage
      Opportunities { OpportunityId Topic PotentialCustomer EstRevenue PricePerStore
                      OpportunitySiteType EstimatedCloseDate CreatedOn }
    }
  }`;
  const data = await graphqlClient.request<any>(query);
  const res = data.getCrmOpportunities;
  if (!res || res.Status !== 'Success') return [];
  return res.Opportunities || [];
}

/** Invoiced revenue per store per year. udm_invoice has no brand relationship,
 *  so join through the stores' customer_id, then aggregate client-side to keep
 *  the CSV compact (raw invoices can run to hundreds of thousands of rows). */
async function fetchRevenue(brand: string, isUP: boolean): Promise<any[]> {
  const storeWhere = isUP
    ? `{ brand: { ultimate_parent: { _ilike: "%${brand}%" } }, customer_id: { _is_null: false } }`
    : `{ brand_name: { _ilike: "%${brand}%" }, customer_id: { _is_null: false } }`;
  const stores = await fetchAllRows('udm_poi', storeWhere, '{ id: asc }', ['customer_id']);
  const ids = Array.from(new Set(stores.map(s => s.customer_id).filter(Boolean)));

  const byStoreYear = new Map<string, { customer_id: string; year: string; invoices: number; revenue_usd: number }>();
  const CHUNK = 100;
  for (let i = 0; i < ids.length; i += CHUNK) {
    const chunk = ids.slice(i, i + CHUNK).map(id => JSON.stringify(id)).join(', ');
    const invoices = await fetchAllRows(
      'udm_invoice',
      `{ customer_id: { _in: [${chunk}] } }`,
      '{ id: asc }',
      ['customer_id', 'date', 'total_amount'],
    );
    for (const inv of invoices) {
      const year = String(inv.date || '').slice(0, 4) || 'unknown';
      const key = `${inv.customer_id}|${year}`;
      const row = byStoreYear.get(key)
        || { customer_id: inv.customer_id, year, invoices: 0, revenue_usd: 0 };
      row.invoices += 1;
      row.revenue_usd += Number(inv.total_amount) || 0;
      byStoreYear.set(key, row);
    }
  }
  return Array.from(byStoreYear.values())
    .map(r => ({ ...r, revenue_usd: Math.round(r.revenue_usd * 100) / 100 }))
    .sort((a, b) => a.customer_id.localeCompare(b.customer_id) || a.year.localeCompare(b.year));
}

/** udm_updated_store_id only carries brand_name, so resolve an ultimate
 *  parent to its banner names first so sub-brand rows are included. */
async function fetchUpdatedStoreIds(brand: string, isUP: boolean): Promise<any[]> {
  let nameFilter: string;
  if (isUP) {
    const brands = await fetchAllRows(
      'udm_brand', `{ ultimate_parent: { _ilike: "%${brand}%" } }`, '{ name: asc }', ['name'],
    );
    const names = brands.map(b => b.name).filter(Boolean);
    if (names.length === 0) return [];
    nameFilter = `{ brand_name: { _in: [${names.map(n => JSON.stringify(n)).join(', ')}] } }`;
  } else {
    nameFilter = `{ brand_name: { _ilike: "%${brand}%" } }`;
  }
  return fetchAllRows('udm_updated_store_id', nameFilter, '{ id: asc }',
    ['id', 'poi_id', 'store_id', 'brand_name', 'address', 'zipcode']);
}

function buildClaudeMd(brand: string, isUP: boolean, written: { d: DatasetDef; count: number }[]) {
  let md = `# ${brand} — Atlas Cowork Project\n\n`;
  md += `Exported Atlas data for ${brand}${isUP ? ' (ultimate parent)' : ''}. `;
  md += `Use the skills in \`.claude/skills/\` to generate sales materials.\n\n`;
  md += `## Available data\n\nAll CSV files are in the \`data/\` folder.\n\n`;
  for (const { d, count } of written) {
    md += `### data/${d.file}\n${d.description}\nRows: ${count}. Columns: ${d.columns.join(', ')}.\n\n`;
  }
  md += `## Branding (apply by default)\n`;
  md += `ALWAYS style every visual deliverable — documents, slides, PDFs, HTML, and `;
  md += `app UI — with the **gatekeeper-design** skill in \`.claude/skills/gatekeeper-design/\`, `;
  md += `even when the user does not explicitly ask for it. Use navy \`#0E2E5C\` + safety `;
  md += `green \`#6FA63A\`, the Work Sans font, and the Gatekeeper logo; trademark product `;
  md += `names (Purchek®, SmartWheel®, FaceFirst®). See its \`colors_and_type.css\` and \`README.md\`.\n\n`;
  md += `## How to use\n`;
  md += `- "Make a sales proposal for ${brand}"      → sales-proposal skill\n`;
  md += `- "Generate an ROI report"                  → roi-report skill\n`;
  md += `- "Which stores are the best opportunity?"  → deal-analysis skill\n`;
  md += `- "Give me the contact list"                → contact-formatter skill\n`;
  md += `- "Evaluate the proposal" / "Does this look right?" → skill-evaluator skill\n`;
  return md;
}

export interface OpenInClaudeOptions {
  brand: string;
  isUltimateParent: boolean;
  selectedKeys: string[];   // which datasets to include
  root?: string;            // project root (defaults to ~/Atlas on the Rust side)
}

/**
 * Full Type 2 flow: fetch selected data → build CSVs + Claude.md →
 * Tauri writes the project + injects skills → open the folder.
 * Returns the created project path.
 */
export async function openInClaude(opts: OpenInClaudeOptions): Promise<string> {
  const { brand, isUltimateParent, selectedKeys, root } = opts;

  const chosen = DATASETS.filter(d => selectedKeys.includes(d.key));
  const csvs: { name: string; content: string }[] = [];
  const written: { d: DatasetDef; count: number }[] = [];

  for (const d of chosen) {
    const rows = await fetchDataset(d, brand, isUltimateParent);
    if (rows.length === 0) continue; // skip empty datasets
    csvs.push({ name: d.file, content: toCSV(d.columns, rows) });
    written.push({ d, count: rows.length });
  }

  const claudeMd = buildClaudeMd(brand, isUltimateParent, written);

  const projectPath = await invoke<string>('create_cowork_project', {
    root,
    brand,
    csvs,
    claudeMd,
    // For the project's .mcp.json — the MCP server queries the same env.
    hasuraUrl: environment.graphqlEndpoint,
    // Where the app downloads/refreshes Cowork skills from (frontend origin).
    skillsBaseUrl: environment.skillsBaseUrl,
  });

  return projectPath;
}

/** Launch Claude Desktop directly into a new Cowork session with the project
 *  folder attached (claude://cowork/new?folder=…). Claude shows a one-time
 *  trust prompt for the folder — that's enforced by Claude and can't be skipped. */
export async function launchClaudeWithProject(path: string): Promise<void> {
  await invoke('launch_claude', { folder: path }).catch(() => {});
}

export function coworkProjectExists(brand: string, root?: string): Promise<boolean> {
  return invoke<boolean>('cowork_project_exists', { root, brand });
}
